import { useCallback } from 'react';
import { REST_FALLBACK_SEC, type Exercise } from '@heraion/shared';

import type { LoggerHostDeps } from './hostDeps';
import type { LoggerDraft } from './state/loggerDraftTypes';
import type { EditingTarget } from './LoggerScreen';
import { sessionKeyOf, type LoggerMode } from './loggerMode';
import { useHoldTimer } from './useHoldTimer';
import { useStopAllTimers } from './useStopAllTimers';
import { useRestPicker } from './useRestPicker';
import { useTimerDefaults } from './useTimerDefaults';
import type { UseRestTimer } from './useRestTimer';
import { learnedRest } from './state/learnedRestStore';

type UseLoggerTimersArgs = {
  draft: LoggerDraft;
  editing: EditingTarget | null;
  mode: LoggerMode;
  catalog: Exercise[] | null;
  setCatalog: React.Dispatch<React.SetStateAction<Exercise[] | null>>;
  restTimer: UseRestTimer;
  t: (k: string, opts?: Record<string, unknown>) => string;
  deps: LoggerHostDeps;
};

// The timer side of useLoggerScreen (own hook, 500-line cap): the hold timer,
// the stop-all, the two default-timer sheets and the manual rest start. The
// rest timer itself stays in the screen hook, the pill cycle reads it first.
// Moved VERBATIM, hook-call order unchanged.
export function useLoggerTimers({
  draft,
  editing,
  mode,
  catalog,
  setCatalog,
  restTimer,
  t,
  deps,
}: UseLoggerTimersArgs) {
  const { profile, refreshProfile } = deps;
  // Hold/cardio timer for duration sets. It owns the COUNT only: the seconds
  // and the completion belong to the effort meter now, so no dispatch and no
  // cycleSetPill here (MX-5 slice 3, one completion path for every set kind).
  const hold = useHoldTimer({
    entries: draft.entries,
    catalog,
    profileDefaultDurationSec: profile.defaultDurationSec,
    timerNotification: deps.timerNotification,
    playTimerCue: deps.playTimerCue,
  });
  // Every timer stops on a new session, a save or a discard (useStopAllTimers).
  const stopAllTimers = useStopAllTimers({
    restTimer, hold, timerNotification: deps.timerNotification, sessionKey: sessionKeyOf(draft, editing, mode),
  });

  // Quick-edit Exercise.defaultRestSec from the rest banner without
  // leaving the workout. Antenore's 2026-05-20 complaint: the catalog
  // edit-default-rest path lived only on web and was buried behind
  // Settings → Catalog → exercise → edit. The banner button below
  // surfaces the same control mid-workout: tap → slider →
  // saveExerciseTimerDefaults (the athlete's own timer row, #411).
  // Updates the in-memory catalog so the
  // next set on this exercise picks up the new default without a
  // full page reload; the catalog source is marked stale so the next
  // read revalidates (#342).
  // Rest-default picker (1.82.0 slider Modal). State + the write
  // side-effects live in useRestPicker; the screen wires the returned
  // handlers into the rest banner button + RestDefaultPicker. See
  // Track R.
  const restPicker = useRestPicker({
    catalog,
    setCatalog,
    profileDefaultRestSec: profile.defaultRestSec,
    refreshProfile,
    activeRestExerciseId: restTimer.state.exerciseId,
    t,
    durableWrite: deps.durableWrite,
    patchProfile: deps.patchProfile,
    notify: deps.notify,
    onCatalogSaved: () => deps.catalog.invalidate(),
  });
  // In-Logger per-exercise timer-defaults sheet (the ⚙ on the start-timer
  // affordance), sets rest + countdown/hold default for the active exercise
  // mid-workout. Shares the PATCH with the Settings card via
  // saveExerciseTimerDefaults; LoggerModals mounts the ExerciseTimerSheet.
  const timerDefaults = useTimerDefaults({
    catalog,
    setCatalog,
    profileDefaultRestSec: profile.defaultRestSec,
    profileDefaultDurationSec: profile.defaultDurationSec,
    t,
    durableWrite: deps.durableWrite,
    notify: deps.notify,
    onSaved: () => deps.catalog.invalidate(),
  });
  // Manual rest start (inbox 0b1a7474): the header clock arms a rest on demand
  // with the profile default. ⭐ TM-19, it BINDS that rest to the exercise the
  // caller names, undoing the earlier "unbound on purpose": the tag is what
  // FocusPanelRest reads to draw the ⚙, and what the picker needs to know which
  // catalog row to PATCH, so an untagged rest silently removed both. The slug
  // comes from the cursor, which lives above this hook (LoggerScreen resolves
  // it), same reason useRestAdvance is called up there.
  const startManualRest = useCallback(
    (exerciseId?: string) => {
      // A rest by hand follows no set, so it teaches nothing (#40).
      learnedRest.arm(exerciseId, false);
      restTimer.start(profile.defaultRestSec ?? REST_FALLBACK_SEC, exerciseId);
    },
    [restTimer, profile.defaultRestSec],
  );

  return { hold, stopAllTimers, restPicker, timerDefaults, startManualRest };
}
